import { Injectable } from "@angular/core";
import { BaseService, HttpHeadersBuilder } from "../@base/base.service";
import { filter, Observable, Subject } from "rxjs";
import {
  DownloadWorkSheetDealerBirthDatesQueryResponse,
  GetDealerBirthDateDetailsQueryResponse,
  ListDealerBirthDatesQueryResponse,
  DownloadWorksheetPortfolioManagementDetailsQueryResult,
  GetPortfolioDetailsReportDataModel,
  ListDealerBirthDatesRequest,
  ListPortfolioManagementCountQuery,
  ListPortfolioManagementCountQueryResponse,
  PieGraphData,
  ProductData,
  ReportFilterData,
  GetSalesReportQuery,
  GetSalesReportQueryResponse,
  GetSalesReportDetailsForComissionersQuery,
  GetSalesReportDetailsForComissionersQueryResponse,
  GetPaymentContemplationReportQueryResponse,
  DownloadWorksheetContemplationPaymentQueryResponse,
  ListCommissionsPendingInvoiceRequest,
  ListCommissionsPendingInvoiceQueryResponse,
  ListCommissionsPendingInvoiceDetailsRequest,
  ListConversionBonusRequest,
  ListConversionBonusResponse,
  ListCommissionsPendingInvoiceDetailResponse,
  ListWitheldComissionsForComissionersQueryResponse,
  ListWithheldCommissionsForCommissionersQuery,
  GetCommissionsSummaryRequest,
  CommissionsSummaryResponse,
  GetReceivablesConciliationQueryResponse,
  GetReceivablesConciliationDetailsQueryResponse,
  GetReceivablesConciliationItem,
} from "../models/reports/reports";
import { RecurrentFetch } from "../helpers/recurrent-fetcher";
import { Paginator, PaginatorItems } from "../models/table";
import { Period } from "../models/period";
import { ParametersService } from "./parameters.service";
import { GraphResult } from "@models/graph";
import { PortfolioActives } from "@enums/portfolio-actives";
import { minutesToMilliseconds } from "date-fns";
import { MINUTES } from "../const/time";
import { FileResponse } from "@models/settings/settings";
import { CommissionEnum } from "@enums/commission";
import { DateHelper } from "@helpers/data-helper";

@Injectable({
  providedIn: "root",
})
export class ReportsService extends BaseService<any> {

  //#region Feature Usage Recurrent Fetchers Fields

  private tableUsageReportRecurrentFetcher: RecurrentFetch<any>;
  private tableRecurrentFetcher: RecurrentFetch<any>;
  private graphsRecurrentFetcher: RecurrentFetch<any>;

  //#endregion

  //#region Feature Usage Stream Subjects

  public tableUsageReportStreamSubject = new Subject<PaginatorItems<ReportFilterData>>();
  public graphUsageReportsStreamSubject = new Subject<GraphResult[]>();

  public salesReportStramSubject = new Subject<GetSalesReportQueryResponse[]>();

  public tableStreamSubject: Subject<PaginatorItems<ReportFilterData>> =
    new Subject<PaginatorItems<ReportFilterData>>();
  public productsChartStreamSubject: Subject<ProductData[]> = new Subject<
    ProductData[]
  >();
  public pieBankChartStreamSubject: Subject<PieGraphData[]> = new Subject<
    PieGraphData[]
  >();
  public pieInsuranceChartStreamSubject: Subject<PieGraphData[]> = new Subject<
    PieGraphData[]
  >();
  public pieConsortiumChartStreamSubject: Subject<PieGraphData[]> = new Subject<
    PieGraphData[]
  >();
  public pieLiberacredChartStreamSubject: Subject<PieGraphData[]> = new Subject<
    PieGraphData[]
  >();

  //#endregion

  constructor(private readonly parameterService: ParametersService) {
    super();
    this.path = "gateway/reports";
  }

  //#region Status Fields
  public get isFetching() {
    return (
      this.tableRecurrentFetcher?.isFetching ||
      this.graphsRecurrentFetcher?.isFetching
    );
  }

  public get isActive() {
    return (
      this.tableRecurrentFetcher?.isActive ||
      this.graphsRecurrentFetcher?.isActive
    );
  }
  //#endregion

  //#region Feature Usage Report
  public getGraphReports(refetchInMinutes: number, period: Period): void {
    const api: Observable<ProductData[]> = this.http.get<ProductData[]>(
      `${this.api}${this.path}/graphs?${this.generateGraphsDataQueryParameters(
        period
      )}`
    );

    if (!this.graphsRecurrentFetcher) {
      this.graphsRecurrentFetcher = RecurrentFetch.FetchIn(
        () => api,
        refetchInMinutes,
        {
          resolve: (result) => this.publishGraphsData(result),
          error: (e) => console.error(e),
        }
      );
      this.graphsRecurrentFetcher.startFetch();
      return;
    }

    this.graphsRecurrentFetcher.resetFetch(refetchInMinutes, () => api);
  }

  public getUsageReportTableData(
    refetchInMinutes: number,
    period: Period,
    pageNumber: number,
    pageSize: number
  ) {
    const api = super.get(`featureUsage/table?${this.generateTableDataQueryParameters(period, pageNumber, pageSize)}`);

    if (!this.tableRecurrentFetcher) {
      this.tableRecurrentFetcher = RecurrentFetch.FetchIn(
        () => api,
        refetchInMinutes,
        {
          resolve: (result) => this.publishUsageReportTableData(result as any),
          error: (e) => console.error(e),
        }
      );

      this.tableRecurrentFetcher.startFetch();
      return;
    }

    this.tableRecurrentFetcher.resetFetch(refetchInMinutes, () => api);
  }

  public getUsageReportGraphData(refetchInMinutes: number,
    period: Period,
    pageNumber: number,
    pageSize: number) {
    const api = super.get(`featureUsage/graph?${this.generateTableDataQueryParameters(period, pageNumber, pageSize)}`);

    if (!this.graphsRecurrentFetcher) {
      this.graphsRecurrentFetcher = RecurrentFetch.FetchIn(
        () => api,
        refetchInMinutes,
        {
          resolve: (result) => this.publishUsageReportGraphData(result as any),
          error: (e) => console.error(e),
        }
      );

      this.graphsRecurrentFetcher.startFetch();
      return;
    }

    this.graphsRecurrentFetcher.resetFetch(refetchInMinutes, () => api);
  }

  public getTableData(
    refetchInMinutes: number,
    period: Period,
    pageNumber: number,
    pageSize: number
  ): void {
    const api = this.http.get<ReportFilterData>(
      `${this.api}${this.path}/table?${this.generateTableDataQueryParameters(
        period,
        pageNumber,
        pageSize
      )}`
    );
    if (!this.tableRecurrentFetcher) {
      this.tableRecurrentFetcher = RecurrentFetch.FetchIn(
        () => api,
        refetchInMinutes,
        {
          resolve: (result) => this.publishTableData(result as any),
          error: (e) => console.error(e),
        }
      );

      this.tableRecurrentFetcher.startFetch();
      return;
    }

    this.tableRecurrentFetcher.resetFetch(refetchInMinutes, () => api);
  }

  public exportUsageReportData(period: Period): Observable<string> {
    return this.http.get(
      `${this.api}${this.path}/featureUsage/export?period.startDate=${DateHelper.ignoreTime(period.startDate).toISOString()}&period.endDate=${DateHelper.ignoreTime(period.endDate).toISOString()}`,
      { responseType: "text" }
    );
  }

  public stopRecurency() {
    this.tableRecurrentFetcher = null;
    this.graphsRecurrentFetcher = null;
  }

  private publishTableData(tableData: PaginatorItems<ReportFilterData>) {
    this.tableStreamSubject.next(tableData);
  }

  private publishGraphsData(graphsData: ProductData[]) {
    this.productsChartStreamSubject.next(graphsData);
    this.pieBankChartStreamSubject.next(
      graphsData.find((x) => x.product == "Banco")?.funcionalities
    );
    this.pieInsuranceChartStreamSubject.next(
      graphsData.find((x) => x.product == "Seguros")?.funcionalities
    );
    this.pieConsortiumChartStreamSubject.next(
      graphsData.find((x) => x.product == "Consórcio")?.funcionalities
    );
    this.pieLiberacredChartStreamSubject.next(
      graphsData.find((x) => x.product == "Liberacred")?.funcionalities
    );
  }

  private publishUsageReportTableData(tableData: PaginatorItems<ReportFilterData>) {
    this.tableUsageReportStreamSubject.next(tableData);
  }

  private publishUsageReportGraphData(graphData: GraphResult[]) {
    this.graphUsageReportsStreamSubject.next(graphData);
  }

  // UTILS
  private generateTableDataQueryParameters(
    period: Period,
    pageNumber: number,
    pageSize: number
  ) {
    let parameterString = "";

    parameterString += `pageNumber=${pageNumber}`;
    parameterString += `&pageSize=${pageSize}`;
    parameterString += `&period.startDate=${DateHelper.ignoreTime(period.startDate).toISOString()}`;
    parameterString += `&period.endDate=${DateHelper.ignoreTime(period.endDate).toISOString()}`;

    return parameterString;
  }

  private generateGraphsDataQueryParameters(period: Period) {
    let parameterString = "";

    parameterString += `startDate=${DateHelper.ignoreTime(period.startDate).toISOString()}`;
    parameterString += `&endDate=${DateHelper.ignoreTime(period.endDate).toISOString()}`;

    return parameterString;
  }

  //#endregion

  //#region Birthdate Report
  public getBirthDateReport(query: ListDealerBirthDatesRequest, paginator: Paginator, firstSearchDone: boolean) {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListDealerBirthDatesQueryResponse>>(`birthdates/?${this.generatePaginatedBirthDateReportParams(query)}`, false, firstSearchDone)
  }

  public exportBirthDateReport(query: ListDealerBirthDatesRequest) {
    return super.get<DownloadWorkSheetDealerBirthDatesQueryResponse>(`birthdates/export?${this.generateBirthDateReportParams(query)}`)
  }

  public exportBirthDatePdf(query: ListDealerBirthDatesRequest): Observable<Blob> {
    return this.http.get<Blob>(`${this.api}${this.path}/birthdates/pdf?${this.generateBirthDateReportParams(query)}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  public getPortfolioReport(query: ListPortfolioManagementCountQuery, paginator: Paginator, firstSearchDone: boolean) {
    return super.get<PaginatorItems<ListPortfolioManagementCountQueryResponse>>(`portfolioManagement/?${this.generatePortfolioReportParams(query, paginator)}`, false, firstSearchDone);
  }

  public getPortfolioSummary(query: ListPortfolioManagementCountQuery) {
    return super.get<ListPortfolioManagementCountQueryResponse>(`portfolioManagement/summary?${this.generatePortfolioSummaryParams(query)}`);
  }

  public getPortfolioDetailsReport(identifierCode: string, selledAt: Period, active: PortfolioActives, paginator: Paginator, firstSearchDone: boolean) {
    return super.get<PaginatorItems<GetPortfolioDetailsReportDataModel>>(`portfolioManagement/details/?${this.generatePortfolioDetailsReport(identifierCode, selledAt, active, paginator)}`, false, firstSearchDone);
  }

  public getBirthDateDetailsReportForUser(userId: number) {
    return super.get<GetDealerBirthDateDetailsQueryResponse>(`birthdates/user/${userId}`)
  }

  // UTILS - Refactoring Needed to use a Generic alternative
  private generatePaginatedBirthDateReportParams(query: ListDealerBirthDatesRequest): string {
    let result = "";
    result += `pageNumber=${query.pageNumber}`;
    result += `&pageSize=${query.pageSize}`;
    if (query.birthMonthIndex != 0) {
      result += `&birthMonthIndex=${query.birthMonthIndex}`;
    }

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;


    if (query.startDate != null && query.endDate != null) {
      result += `&startDate=${query.startDate.toISOString()}`;
      result += `&endDate=${query.endDate.toISOString()}`;
    }

    return result;
  }

  private generateBirthDateReportParams(query: ListDealerBirthDatesRequest): string {
    let result = "";
    if (query.birthMonthIndex != 0) {
      result += `&birthMonthIndex=${query.birthMonthIndex}`;
    }

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    if (query.startDate != null && query.endDate != null) {
      result += `&startDate=${query.startDate.toISOString()}`;
      result += `&endDate=${query.endDate.toISOString()}`;
    }

    return result;
  }

  //#endregion

  //#region Portfolio Management Report
  public exportPortfolioDetailsToPdfReport(identifierCode: string, selledAt: Period, active: PortfolioActives) {
    return this.http.get<Blob>(`${this.api}${this.path}/portfolioManagement/details/pdf?${this.generatePortfolioDetailsExportReportParams(identifierCode, selledAt, active)}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  public exportPortfolioDetailsToXlsxReport(identifierCode: string, selledAt: Period, active: PortfolioActives) {
    return super.get<DownloadWorksheetPortfolioManagementDetailsQueryResult>(`portfolioManagement/details/export?${this.generatePortfolioDetailsExportReportParams(identifierCode, selledAt, active)}`);
  }

  // UTILS - Refactoring Needed to use the Generic alternative
  private generatePortfolioDetailsReport(identifierCode: string, selledAt: Period, active: PortfolioActives, paginator: Paginator): string {
    let result = "";
    result += `pageNumber=${paginator.index}`;
    result += `&pageSize=${paginator.pageSize}`;
    result += `&active=${active}`;

    if (!!selledAt.startDate && !!selledAt.endDate) {
      result += `&salePeriod.startDate=${selledAt.startDate.toISOString()}`;
      result += `&salePeriod.endDate=${selledAt.endDate.toISOString()}`;
    }

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(identifierCode)}`;


    return result;
  }

  private generatePortfolioDetailsExportReportParams(identifierCode: string, salePeriod: Period, active: PortfolioActives): string {
    let result = "";

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(identifierCode)}`;

    if (!!salePeriod.startDate && !!salePeriod.endDate) {
      result += `&salePeriod.startDate=${salePeriod.startDate.toISOString()}`;
      result += `&salePeriod.endDate=${salePeriod.endDate.toISOString()}`;
    }

    if (!!active) {
      result += `&active=${active}`;
    }

    return result;
  }

  private generatePortfolioReportParams(query: ListPortfolioManagementCountQuery, pagintor: Paginator) {
    let result = "";
    result += `pageNumber=${pagintor.index}`;
    result += `&pageSize=${pagintor.pageSize}`;

    for (let i = 0; i < query.actives.length; i++) {
      result += `&actives[${i}]=${query.actives[i]}`
    }

    if (!!query.initialDate && !!query.endDate) {
      result += `&salePeriod.startDate=${query.initialDate.toISOString()}`;
      result += `&salePeriod.endDate=${query.endDate.toISOString()}`;
    }

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    return result;
  }

  private generatePortfolioSummaryParams(query: ListPortfolioManagementCountQuery) {
    let result = "";

    for (let i = 0; i < query.actives.length; i++) {
      result += `&actives[${i}]=${query.actives[i]}`
    }

    if (!!query.initialDate && !!query.endDate) {
      result += `&salePeriod.startDate=${query.initialDate.toISOString()}`;
      result += `&salePeriod.endDate=${query.endDate.toISOString()}`;
    }

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    return result;
  }

  //#endregion

  //#region Sales Report
  public getSalesReport(query: GetSalesReportQuery, firstSearchDone: boolean): void {
    const results = super.get<GetSalesReportQueryResponse[]>(`sales?${this.generateSalesReportQueryParams(query)}`, false, firstSearchDone);
    results.subscribe(res => {
      this.salesReportStramSubject.next(res)
    })
  }

  public getSalesReportSummary(query: GetSalesReportQuery, firstSearchDone: boolean): void {
    const results = super.get<GetSalesReportQueryResponse[]>(`sales/summary?${this.generateSalesReportQueryParams(query)}`, false, firstSearchDone);
    results.subscribe(res => {
      this.salesReportStramSubject.next(res)
    })
  }

  public getSalesDetailsReport(query: GetSalesReportDetailsForComissionersQuery, paginator: Paginator, firstSearchDone: boolean): Observable<PaginatorItems<GetSalesReportDetailsForComissionersQueryResponse>> {
    return this.http.get<PaginatorItems<GetSalesReportDetailsForComissionersQueryResponse>>(`${this.api}${this.path}/sales/details?${this.generateSalesDetailsQueryParameters(query, paginator)}`, {
      headers: HttpHeadersBuilder.create()
        .skipFeatureUsageTrack(firstSearchDone)
        .build(),
      timeout: 5 * MINUTES
    } as any) as unknown as Observable<PaginatorItems<GetSalesReportDetailsForComissionersQueryResponse>>;
  }

  public exportSalesDetailsToXlsx(query: GetSalesReportDetailsForComissionersQuery): Observable<DownloadWorkSheetDealerBirthDatesQueryResponse> {
    return this.http.get<DownloadWorkSheetDealerBirthDatesQueryResponse>(`${this.api}${this.path}/sales/details/export?${this.generateExportSalesDetailsParameters(query)}`, {
      timeout: minutesToMilliseconds(5)
    } as any) as unknown as Observable<DownloadWorkSheetDealerBirthDatesQueryResponse>;
  }

  public exportSalesDetailsToPdf(query: GetSalesReportDetailsForComissionersQuery) {
    return this.http.get<Blob>(`${this.api}${this.path}/sales/details/pdf?${this.generateExportSalesDetailsParameters(query)}`, {
      responseType: 'blob',
      timeout: 900000
    } as any) as unknown as Observable<Blob>;
  }

  //UTILS - Refatoring Needed to use the Generic alternative
  private generateExportSalesDetailsParameters(query: GetSalesReportDetailsForComissionersQuery) {
    let result = "";
    result += `plataform=${query.plataform}`;

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    if (!!query.firstPayedInstallmentPeriod?.startDate && !!query.firstPayedInstallmentPeriod?.endDate) {
      result += `&firstPayedInstallmentPeriod.startDate=${query.firstPayedInstallmentPeriod.startDate.toISOString()}`;
      result += `&firstPayedInstallmentPeriod.endDate=${query.firstPayedInstallmentPeriod.endDate.toISOString()}`;
    }

    return result;
  }

  private generateSalesDetailsQueryParameters(query: GetSalesReportDetailsForComissionersQuery, paginator: Paginator) {
    let result = "";
    result += `pageNumber=${paginator.index}`;
    result += `&pageSize=${paginator.pageSize}`;

    result += `&plataform=${query.plataform}`;

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;


    if (!!query.firstPayedInstallmentPeriod?.startDate && !!query.firstPayedInstallmentPeriod?.endDate) {
      result += `&firstPayedInstallmentPeriod.startDate=${query.firstPayedInstallmentPeriod.startDate.toISOString()}`;
      result += `&firstPayedInstallmentPeriod.endDate=${query.firstPayedInstallmentPeriod.endDate.toISOString()}`;
    }

    return result;
  }

  private generateSalesReportQueryParams(query: GetSalesReportQuery) {
    let result = "";

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    if (!!query?.startDate && !!query?.endDate) {
      result += `&firstInstallmentPaymentPeriod.startDate=${query.startDate.toISOString()}`;
      result += `&firstInstallmentPaymentPeriod.endDate=${query.endDate.toISOString()}`;
    }

    return result;
  }

  //#endregion

  //#region Comissions Reports
  public getCommissionSummary(query: GetCommissionsSummaryRequest): Observable<any> {
    return super.get<CommissionsSummaryResponse>(`commissions/summary?${this.toQueryParams(query)}`)
  }

  //#region Generic Comission Report Methods
  public getComissionReport<T>(query: any, paginator: Paginator, route: CommissionEnum) {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<T>>(`commissions/${route}?${this.toQueryParams(query)}`);
  }

  public exportWorksheetReport<T>(query: T, route: CommissionEnum): Observable<FileResponse> {
    return super.get<FileResponse>(`commissions/${route}/export?${this.toQueryParams(query)}`)
  }

  public exportPdfReport<T>(query: T, route: CommissionEnum): Observable<Blob> {
    return this.http.get<Blob>(`${this.api}${this.path}/commissions/${route}/pdf?${this.toQueryParams(query)}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  public exportWorksheetDetailReport(query: ListConversionBonusRequest, route: CommissionEnum): Observable<FileResponse> {
    return super.get<FileResponse>(`commissions/${route}/details/export?${this.toQueryParams(query)}`)
  }

  public exportPdfDetailReport(query: ListConversionBonusRequest, route: CommissionEnum): Observable<Blob> {
    return this.http.get<Blob>(`${this.api}${this.path}/commissions/${route}/details/pdf?${this.toQueryParams(query)}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  //#endregion

  //#region Pending Invoice Comission Report
  public getCommissionsPendingInvoiceReport(query: ListCommissionsPendingInvoiceRequest, paginator: Paginator): Observable<PaginatorItems<any>> {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListCommissionsPendingInvoiceQueryResponse>>(`commissions/pending-invoices?${this.toQueryParams(query)}`)
  }

  public getCommissionsPendingInvoiceDetailsReport(query: ListCommissionsPendingInvoiceDetailsRequest, paginator: Paginator): Observable<PaginatorItems<any>> {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListCommissionsPendingInvoiceDetailResponse>>(`commissions/pending-invoices/details?${this.toQueryParams(query)}`)
  }

  public getCommissionsDetailsReport(query: ListCommissionsPendingInvoiceDetailsRequest, paginator: Paginator, route: CommissionEnum): Observable<PaginatorItems<any>> {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListCommissionsPendingInvoiceDetailResponse>>(`commissions/${route}/details?${this.toQueryParams(query)}`)
  }

  //#endregion

  //#region Conversion Bonus Comission Report

  public getConversionBonusReport(query: ListConversionBonusRequest, paginator: Paginator, route?: string) {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListConversionBonusResponse>>(`commissions/conversion-bonus/${route || ""}?${this.toQueryParams(query)}`);
  }

  //#endregion

  //#region  Future Comission Report

  public getFutureComissionReport(query: ListConversionBonusRequest, paginator: Paginator, route?: string) {
    query.pageNumber = paginator.index;
    query.pageSize = paginator.pageSize;

    return super.get<PaginatorItems<ListConversionBonusResponse>>(`commissions/future-comission/${route || ""}?${this.toQueryParams(query)}`);
  }

  //#endregion

  //#region Payment Contemplation Report

  public getPaymentContemplationReportData(identifierCode: string, contemplationPeriod: Period, paginator: Paginator, firstSearchDone: boolean) {
    return super.get<PaginatorItems<GetPaymentContemplationReportQueryResponse>>(`contemplationPayment?${this.generateContemplatioPaymentParameters(identifierCode, contemplationPeriod, paginator)}`, false, firstSearchDone)
  }

  public exportPaymentContemplationReportDataXlsx(identifierCode: string, contemplationPeriod: Period) {
    return super.get<DownloadWorksheetContemplationPaymentQueryResponse>(`contemplationPayment/export/?${this.generateContemplatioPaymentExportParameters(identifierCode, contemplationPeriod)}`)
  }

  public exportPaymentContemplationReportDataPdf(identifierCode: string, contemplationPeriod: Period) {
    return this.http.get<Blob>(`${this.api}${this.path}/contemplationPayment/pdf/?${this.generateContemplatioPaymentExportParameters(identifierCode, contemplationPeriod)}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  //UTILS - Refactoring Needed to use Generic method
  private generateContemplatioPaymentParameters(identifierCode: string, contemplationPeriod: Period, paginator: Paginator): string {
    let query = "";

    query += `pageNumber=${paginator.index}`;
    query += `&pageSize=${paginator.pageSize}`;

    query += `&situationPeriod.startDate=${contemplationPeriod.startDate.toISOString()}`
    query += `&situationPeriod.endDate=${contemplationPeriod.endDate.toISOString()}`;

    query += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(identifierCode)}`;

    return query;
  }

  private generateContemplatioPaymentExportParameters(identifierCode: string, contemplationPeriod: Period): string {
    let query = "";

    query += `situationPeriod.startDate=${contemplationPeriod.startDate.toISOString()}`
    query += `&situationPeriod.endDate=${contemplationPeriod.endDate.toISOString()}`;

    query += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(identifierCode)}`;

    return query;
  }

  //#endregion

  //#region Witheld Comissions Report
  public getWithheldComissionsReport(query: ListWithheldCommissionsForCommissionersQuery, paginator: Paginator) {
    return super.get<PaginatorItems<ListWitheldComissionsForComissionersQueryResponse>>(`comissions/witheld/?${this.generateWithhelComissionsReportFilter(query, paginator)}`);
  }

  // UTILS - Refactoring Needed to use the Generic alternative
  private generateWithhelComissionsReportFilter(query: ListWithheldCommissionsForCommissionersQuery, paginator: Paginator): string {
    let result = ""

    result += `pageNumber=${paginator.index}`;
    result += `&pageSize=${paginator.pageSize}`;

    result += `&salesPeriod.startDate=${query.startDate.toISOString()}`;
    result += `&salesPeriod.endDate=${query.endDate.toISOString()}`;

    result += `&identifierCode=${this.getIdentifierCodeOrAllFromCurrentAccessPoint(query.identifierCode)}`;

    result += `&document=${query.document ?? ""}`;
    result += `&filter=${query.typeQuery}`;

    return result;
  }

  //#endregion

  //#region Receivables Reconciliation Report
  public getReceivablesReconciliationReport(startDate: Date, endDate: Date, identifierCode: string, typeFilter: string[], pageNumber: number, pageSize: number): Observable<GetReceivablesConciliationQueryResponse> {
    return super.get(`receivablesReconciliation?${this.toQueryParams({ startDate, endDate, identifierCode, typeCode: typeFilter, pageNumber, pageSize })}`);
  }

  public exportPdfReceivablesReconciliationReport(startDate: Date, endDate: Date, identifierCode: string, typeFilter: string[]) {
    return this.http.get<Blob>(`${this.api}${this.path}/receivablesReconciliation/pdf?${this.toQueryParams({ startDate, endDate, identifierCode, typeCode: typeFilter })}`, {
      responseType: 'blob'
    } as any) as unknown as Observable<Blob>;
  }

  public exportWorksheetReceivablesReconciliationReport(startDate: Date, endDate: Date, identifierCode: string, typeFilter: string[]) {
    return super.get<FileResponse>(`receivablesReconciliation/export?${this.toQueryParams({ startDate, endDate, identifierCode, typeCode: typeFilter })}`);
  }

  public getReceivablesReconciliationDetailsReport(conciliationReceivable: GetReceivablesConciliationItem, firstSearchDone: boolean, pageNumber: number, pageSize: number): Observable<PaginatorItems<GetReceivablesConciliationDetailsQueryResponse>> {
    return super.get(`receivablesReconciliation/details?${this.toQueryParams({ paymentDate: conciliationReceivable.paymentDate, identifierCode: conciliationReceivable.dealerDocument, quota: conciliationReceivable.quota, pageNumber, pageSize })}`, false, firstSearchDone);
  }

  public exportPdfReceivablesReconciliationDetailsReport(conciliationReceivables: GetReceivablesConciliationItem, firstSearchDone: boolean): Observable<Blob> {
    return this.http.get<Blob>(`${this.api}${this.path}/receivablesReconciliation/details/pdf?${this.toQueryParams({ invoice: conciliationReceivables.invoiceNumber, paymentDate: conciliationReceivables.paymentDate, identifierCode: conciliationReceivables.dealerDocument, quota: conciliationReceivables.quota })}`,
      {
        responseType: 'blob'
      } as any) as unknown as Observable<Blob>;
  }

  public exportWorksheetReceivablesReconciliationDetailsReport(conciliationReceivables: GetReceivablesConciliationItem, firstSearchDone: boolean): Observable<FileResponse> {
    return super.get<FileResponse>(`receivablesReconciliation/details/export?${this.toQueryParams({ paymentDate: conciliationReceivables.paymentDate, identifierCode: conciliationReceivables.dealerDocument, quota: conciliationReceivables.quota })}`, false, firstSearchDone);
  }

  //#endregion

  //#endregion

  private getIdentifierCodeOrAllFromCurrentAccessPoint(identifierCode: string): string {
    if (!identifierCode) {
      return this.getAccessPoint().identifierCode;
    }

    return identifierCode;
  }

  private toQueryParams(request: any): string {
    const params = new URLSearchParams();

    for (const key in request) {
      if (request.hasOwnProperty(key) && request[key] != null) {
        if (key === 'startDate' || key === 'endDate') {
          params.append(key, request[key].toISOString().split('T')[0]);
        }
        else if (key === 'identifierCode') {
          params.append(key, this.getIdentifierCodeOrAllFromCurrentAccessPoint(request[key]));
        } else if (Array.isArray(request[key])) {
          let count = (request[key] as unknown[]).length;
          for (let i = 0; i < count; i++) {
            params.append(`${key}[${i}]`, request[key][i])
          }
        } else {
          params.append(key, request[key].toString());
        }
      }
    }

    return params.toString();
  }
}
